import { proxy } from "valtio";
import { normalizeUserData, mergeUserData } from "../utils/userNormalizer";
import {
  authStoreLogin,
  authStoreLogout,
  authStoreResetPassword,
  authStoreResetPasswordConfirm,
  authStoreSignup,
  authStoreVerifyEmail,
  authStoreResendVerificationEmail,
  authStoreUpdateProfile,
  authStoreInitializeAuth,
  signup,
} from "../api/authApi";
import { AUTH_STATE_KEY, LOGIN_TIMESTAMP_KEY } from "../utils/localStoragekeys";
import { userDataFormat } from "@/utils/userDataFormat";
import { permissionStore } from "./permissionStore";

const applyPermissionsFromUser = (user) => {
  if (!user) return;

  const rawPermissions = user.permissions || user._raw?.permissions;

  if (rawPermissions) {
    let parsed = rawPermissions;
    if (typeof rawPermissions === "string") {
      try {
        parsed = JSON.parse(rawPermissions);
      } catch (error) {
        console.error("Failed to parse stored permissions:", error);
        parsed = null;
      }
    }

    if (parsed && typeof parsed === "object") {
      permissionStore.setPermissions(parsed);
      return;
    }
  }

  if (user.role) {
    permissionStore.setRole(user.role);
  }
};

// Load initial state from localStorage if available
const loadState = () => {
  try {
    const savedState = localStorage.getItem(AUTH_STATE_KEY);
    if (savedState) {
      const parsed = JSON.parse(savedState);
      const normalizedUser = parsed.user
        ? normalizeUserData(parsed.user, "local-storage")
        : null;
      // Only load non-sensitive data
      return {
        isAuthenticated: parsed.isAuthenticated || false,
        user: normalizedUser,
        loading: false,
        error: null,
      };
    }
  } catch (error) {
    console.error("Failed to load auth state:", error);
  }

  // Return default state
  return {
    isAuthenticated: false,
    user: null,
    loading: false,
    error: null,
  };
};

// Create the auth store with initial state
const authState = loadState();

if (authState.user) {
  applyPermissionsFromUser(authState.user);
}

export const authStore = proxy({
  ...authState,

  // Check if session is valid based on login timestamp
  _isSessionValid() {
    const loginTime = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    if (!loginTime) return false;

    const TWELVE_HOURS = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
    const currentTime = Date.now();
    const sessionAge = currentTime - parseInt(loginTime, 10);

    return sessionAge < TWELVE_HOURS;
  },

  // Initialize authentication state
  async initializeAuth() {
    this.loading = true;
    this._saveState();

    try {
      if (!this._isSessionValid()) {
        this.logout();
        return false;
      }
      const userData = await authStoreInitializeAuth();
      const normalizedUser = normalizeUserData(userData?.user || userData, "users/me");

      // Update auth state
      const userDetails = this.user
        ? mergeUserData(this.user, normalizedUser)
        : normalizedUser;
      this.user = userDetails;
      this.isAuthenticated = true;
      this.error = null;

      // Set permissions from user object if available
      if (normalizedUser?.permissions || normalizedUser?._raw?.permissions) {
        applyPermissionsFromUser(normalizedUser);
      } else if (normalizedUser?.role) {
        // Fallback to role-based permissions if no custom permissions
        permissionStore.setRole(normalizedUser.role);
      }

      return this.isAuthenticated;
    } catch (error) {
      console.error("Auth initialization failed:", error);
      this.error = error.message || "Failed to initialize authentication";
      this.logout();
      return false;
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  // Save state to localStorage whenever it changes
  _saveState() {
    try {
      const stateToSave = {
        isAuthenticated: this.isAuthenticated,
        user: this.user ? this.user : null,
        _lastUpdated: new Date().toISOString(),
      };

      localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Failed to save auth state:", error);
    }
  },

  // Clear auth state from localStorage
  _clearState() {
    try {
      localStorage.removeItem(AUTH_STATE_KEY);
      localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
    } catch (error) {
      console.error("Failed to clear auth state:", error);
    }
  },

  // Request password reset
  async resetPassword(email) {
    this.loading = true;
    this.error = null;

    try {
      const data = await authStoreResetPassword(email);
      return data;
    } catch (error) {
      const errorMessage = error.message || "Failed to send reset email";
      this.error = errorMessage;
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  // Confirm password reset with token
  async resetPasswordConfirm(token, newPassword) {
    this.loading = true;
    this.error = null;

    try {
      const data = await authStoreResetPasswordConfirm(token, newPassword);
      return data;
    } catch (error) {
      const errorMessage = error.message || "Failed to reset password";
      this.error = errorMessage;
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  // Step 1: Initial Signup - CDC + Email + Name
  async signup(indianCdc, email, password, name = "") {
    this.loading = true;
    this.error = null;

    try {
      this.user = { email, indianCdc, name };
      this._saveState();
      const data = await authStoreSignup(indianCdc, email, password, name);

      // Store the email for the next step

      return data;
    } catch (error) {
      const errorMessage = error.message || "Signup failed. Please try again.";
      this.error = errorMessage;
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  async verifyEmail(token) {
    this.loading = true;
    this.error = null;
    try {
      const data = await authStoreVerifyEmail(token);

      // Update auth state with user data
      this.isAuthenticated = false;
      this.user = data.user;
      this._saveState();

      return data;
    } catch (error) {
      const errorMessage =
        error.message || "Email verification failed. Please try again.";
      this.error = errorMessage;
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  // Step 2: Setup Password with token
  async setupPassword(token, password, confirmPassword) {
    this.loading = true;
    this.error = null;

    try {
      const data = await authStoreSetupPassword(
        token,
        password,
        confirmPassword
      );

      // Update auth state
      this.isAuthenticated = true;
      this.user = data.user;
      this._saveState();

      return data;
    } catch (error) {
      const errorMessage =
        error.message || "Password setup failed. Please try again.";
      this.error = errorMessage;
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  // Resend verification email
  async resendVerificationEmail(email) {
    this.loading = true;
    this.error = null;

    try {
      const data = await authStoreResendVerificationEmail(email);
      return data;
    } catch (error) {
      const errorMessage =
        error.message || "Failed to resend verification email";
      this.error = errorMessage;
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  // Update user profile
  async updateProfile(profileData) {
    this.loading = true;
    this.error = null;

    try {
      const data = await authStoreUpdateProfile(profileData);

      // Update local user data with normalized data
      const normalizedData = normalizeUserData(data, "profile-update");

      // Ensure name and email are properly set in both root and profile for backward compatibility
      if (!normalizedData.profile) {
        normalizedData.profile = {};
      }

      // If name/email were updated, ensure they're in the profile object
      if (profileData.name !== undefined) {
        normalizedData.profile.name = profileData.name;
      }
      if (profileData.email !== undefined) {
        normalizedData.profile.email = profileData.email;
      }

      this.user = mergeUserData(this.user, userDataFormat(normalizedData));
      this._saveState();

      return normalizedData;
    } catch (error) {
      const errorMessage = error.message || "Failed to update profile";
      this.error = errorMessage;
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
      this._saveState();
    }
  },

  async login(email, password) {
    this.loading = true;
    this.error = null;
    this._saveState();

    try {
      const data = await authStoreLogin({ email, password });

      // Normalize user data from login response
      const normalizedUser = normalizeUserData(data.user, "login");

      // Ensure name and email are properly set in both root and profile for backward compatibility
      if (normalizedUser) {
        // If profile doesn't exist, create it
        if (!normalizedUser.profile) {
          normalizedUser.profile = {};
        }

        // Ensure name and email are in the profile object
        if (normalizedUser.name) {
          normalizedUser.profile.name = normalizedUser.name;
        }
        if (normalizedUser.email) {
          normalizedUser.profile.email = normalizedUser.email;
        }
      }

      // Update auth state
      this.user = userDataFormat(normalizedUser);
      this.isAuthenticated = true;
      this.error = null;

      // Set permissions from user object if available
      if (normalizedUser?.permissions || normalizedUser?._raw?.permissions) {
        applyPermissionsFromUser(normalizedUser);
      } else if (normalizedUser?.role) {
        // Fallback to role-based permissions if no custom permissions
        permissionStore.setRole(normalizedUser.role);
      }

      // Set login timestamp for 12-hour session
      localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());

      // Save to localStorage
      this._saveState();

      return data;
    } catch (error) {
      const errorMessage = error.message || "Login failed";
      console.error("Login error:", error);
      this.error = errorMessage;
      this._saveState();
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
    }
  },

  async logout() {
    try {
      await authStoreLogout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear all auth state
      this.isAuthenticated = false;
      this.user = null;
      this.error = null;
      this._clearState();
      // Reset permissions to default
      permissionStore.setRole('SEAFARER');
    }
  },
});
